import { useState, useEffect } from 'react';
import { getChartHtsData, getChartMtsData } from '../../api/advanceChart.api';
import { getPeriodType } from '../../Widgets/AdvanceChartWidget/chartProtocol';
import { IntervalKey, PeriodKey } from '../../Widgets/AdvanceChartWidget/types';

export function formatDateYMD(d: Date): string {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const dd = String(d.getDate()).padStart(2, '0');
  return `${y}${m}${dd}`;
}

export function calcFromDate(period: PeriodKey, base: Date): Date {
  const d = new Date(base);
  d.setHours(0, 0, 0, 0);
  switch (period) {
    case '1D':
      d.setDate(d.getDate() - 1);
      break;
    case '2D':
      d.setDate(d.getDate() - 2);
      break;
    case '5D':
      d.setDate(d.getDate() - 5);
      break;
    case 'MTD':
      d.setDate(1);
      break;
    case '1M':
      d.setMonth(d.getMonth() - 1);
      break;
    case '2M':
      d.setMonth(d.getMonth() - 2);
      break;
    case '3M':
      d.setMonth(d.getMonth() - 3);
      break;
    case '6M':
      d.setMonth(d.getMonth() - 6);
      break;
    case 'YTD':
      d.setMonth(0);
      d.setDate(1);
      break;
    case '1Y':
      d.setFullYear(d.getFullYear() - 1);
      break;
    case '2Y':
      d.setFullYear(d.getFullYear() - 2);
      break;
    case '3Y':
      d.setFullYear(d.getFullYear() - 3);
      break;
    case '4Y':
      d.setFullYear(d.getFullYear() - 4);
      break;
    case '5Y':
      d.setFullYear(d.getFullYear() - 5);
      break;
    case '10Y':
      d.setFullYear(d.getFullYear() - 10);
      break;
  }
  return d;
}

export interface UseChartDataResult {
  chartData: string;
  isLoading: boolean;
  error: string | null;
}

const useChartData = (
  symbol: string,
  interval: IntervalKey,
  period: PeriodKey,
  token: string = '',
): UseChartDataResult => {
  const [chartData, setChartData] = useState<string>('');
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!symbol) {
      setChartData('');
      return;
    }

    let cancelled = false;
    setIsLoading(true);
    setError(null);

    const isMts = getPeriodType(interval) === 'mts';
    const today = new Date();
    const fromDate = calcFromDate(period, today);
    const toYMD = formatDateYMD(today);
    const fromYMD = formatDateYMD(fromDate);

    const fetchFn = isMts
      ? getChartMtsData(
          symbol,
          interval,
          fromYMD + '0800',
          toYMD + '1630',
          token,
        )
      : getChartHtsData(symbol, interval, fromYMD, toYMD, token);

    fetchFn
      .then((data) => {
        if (!cancelled) {
          setChartData(data);
        }
      })
      .catch(() => {
        if (!cancelled) {
          setError('Failed to load chart data');
          setChartData('');
        }
      })
      .finally(() => {
        if (!cancelled) setIsLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [symbol, interval, period, token]);

  return { chartData, isLoading, error };
};

export default useChartData;
